import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keeps a register row editable while the rest of the screen keeps refreshing.
 *
 * Saving one field re-reads the whole scheme from the server, and without this
 * the incoming snapshot would overwrite a neighbouring field that is still being
 * typed into. So the row holds its own copy, refuses to take a server value
 * while it has unsaved edits, and — because `commit` sends the difference
 * between the whole row and the server's version — a change that was typed
 * during a save still goes across on the next one.
 */
export function useRowEditor<T extends object>(
  source: T,
  save: (patch: Partial<T>) => Promise<unknown>,
) {
  const [row, setRow] = useState<T>(source);
  const rowRef = useRef(row);
  rowRef.current = row;

  /** Bumped by every edit; a save only clears the dirty flag if nothing changed meanwhile. */
  const editSeq = useRef(0);
  const savedSeq = useRef(0);

  const isDirty = () => editSeq.current !== savedSeq.current;

  useEffect(() => {
    if (!isDirty()) setRow(source);
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Records a keystroke. Nothing is sent until the field is left. */
  const set = useCallback((patch: Partial<T>) => {
    editSeq.current += 1;
    setRow((r) => ({ ...r, ...patch }));
  }, []);

  const diff = useCallback((): Partial<T> => {
    const cur = rowRef.current;
    const patch: Partial<T> = {};
    (Object.keys(cur) as (keyof T)[]).forEach((k) => {
      if (String(cur[k] ?? '') !== String(source[k] ?? '')) patch[k] = cur[k];
    });
    return patch;
  }, [source]);

  /** Sends everything this row has changed. Call it when a field loses focus. */
  const commit = useCallback(async () => {
    const seq = editSeq.current;
    const patch = diff();
    if (!Object.keys(patch).length) {
      savedSeq.current = seq;
      return;
    }
    await save(patch);
    if (editSeq.current === seq) savedSeq.current = seq;
  }, [diff, save]);

  /**
   * Applies and sends a change at once — for a dropdown, where there is no
   * typing to wait for. Anything typed into a neighbouring field but not yet
   * sent goes with it, so choosing from a dropdown can never discard it.
   */
  const commitNow = useCallback(
    async (patch: Partial<T>) => {
      editSeq.current += 1;
      const seq = editSeq.current;
      const full = { ...diff(), ...patch };
      setRow((r) => ({ ...r, ...patch }));
      await save(full);
      if (editSeq.current === seq) savedSeq.current = seq;
    },
    [diff, save],
  );

  return { row, set, commit, commitNow };
}
